import isEqual from "fast-deep-equal/es6";
import { create } from "zustand";
import { OmgevingService } from "../services/omgevingService";
import { FetchState } from "./util";

/**
 * Een officiële bekendmaking uit het register van de omgevingsmonitor. De
 * velden zijn het contract van de monitor en blijven daarom Nederlands.
 */
export type Publication = {
  publicatieId: string;
  titel: string;
  url: string;
  soort: string;
  blad: string;
  creator: string;
  gemeente: string;
  gepubliceerdOp: string | null;
  geometrielabel: string;
  afstandMeter: number | null;
  /** Individuele toestemming of beleid. De monitor bepaalt dat, niet wij. */
  vergunning: boolean;
  /** `PROCEDURESTATUSSEN` van de monitor, uit de titel (omgevingsmonitor#86). */
  procedurestatus?: string;
  /** Herkende activiteiten ("kap", "tijdelijke woonunit"): weergavelabels, geen slugs. */
  activiteiten?: string[];
};

interface PublicationsState {
  state?: FetchState;
  geometry?: GeoJSON.Geometry;
  publications?: Publication[];
  load: (geometry: GeoJSON.Geometry, force?: boolean) => void;
  clear: () => void;
}

/**
 * Los van de cube: die komt uit ca_reference en wordt per project gecachet,
 * dit komt uit de omgevingsmonitor en verandert dagelijks. Ze samenvoegen zou
 * betekenen dat een nieuwe bekendmaking pas zichtbaar wordt als de hele cube
 * ververst.
 *
 * De sleutel is de geometrie, niet een project: hetzelfde perceel levert
 * dezelfde lijst of je nu op /location kijkt, in een projectrapport of in de
 * kaveldrawer. Vergelijken gaat met `fast-deep-equal`, zoals de cube-store
 * dat met zijn coördinaten doet.
 */
export const usePublications = create<PublicationsState>((set, get) => ({
  load: async (geometry: GeoJSON.Geometry, force?: boolean) => {
    const cur = get();
    if (!force && isEqual(cur.geometry, geometry) && cur.state === "fetched") return;
    set({ geometry, state: "fetching", publications: undefined });
    try {
      const publications = await OmgevingService.publications(geometry);
      // Een ander perceel geselecteerd terwijl dit antwoord onderweg was.
      if (isEqual(get().geometry, geometry)) set({ state: "fetched", publications });
    } catch {
      if (isEqual(get().geometry, geometry)) set({ state: "error" });
    }
  },
  clear: () => set({ state: undefined, geometry: undefined, publications: undefined }),
}));
